import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  Search, ArrowUpRight, BookOpen, CreditCard, Users, Brain, CalendarDays,
  FileText, Zap, BarChart3, Star, Settings2, Plug, ShieldCheck, Sparkles,
  ChevronRight,
} from "lucide-react";
import { SiteLayout } from "@/components/marketing/SiteLayout";
import { getHelpTree, type HelpCategory } from "@/lib/help.functions";

export const Route = createFileRoute("/help/")({
  loader: () => getHelpTree({ data: { locale: "en" } }),
  head: () => {
    const url = "https://banana.cards/help";
    const title = "Help Center — banana.cards";
    const description = "Guides, tutorials and answers for Banana Cards. Learn how to use digital cards, contacts CRM, AI brain, bookings, contracts, integrations and more.";
    const collectionLd = {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "Help Center",
      description,
      url,
      inLanguage: "en",
      isPartOf: { "@type": "WebSite", name: "Banana Cards", url: "https://banana.cards" },
    };
    const breadcrumbLd = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://banana.cards" },
        { "@type": "ListItem", position: 2, name: "Help Center", item: url },
      ],
    };
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: "Everything you need to get the most out of Banana Cards." },
        { property: "og:type", content: "website" },
        { property: "og:url", content: url },
        { property: "og:site_name", content: "Banana Cards" },
        { property: "og:locale", content: "en" },
        { property: "og:locale:alternate", content: "vi" },
        { name: "twitter:card", content: "summary" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
      ],
      links: [
        { rel: "canonical", href: url },
        { rel: "alternate", hreflang: "en", href: "https://banana.cards/help" },
        { rel: "alternate", hreflang: "vi", href: "https://banana.cards/vi/help" },
        { rel: "alternate", hreflang: "x-default", href: "https://banana.cards/help" },
      ],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(collectionLd) },
        { type: "application/ld+json", children: JSON.stringify(breadcrumbLd) },
      ],
    };
  },
  component: HelpIndex,
});

const ICONS: Record<string, any> = {
  "getting-started": Sparkles,
  cards: CreditCard,
  contacts: Users,
  ai: Brain,
  bookings: CalendarDays,
  contracts: FileText,
  automations: Zap,
  analytics: BarChart3,
  reviews: Star,
  team: Settings2,
  integrations: Plug,
  account: ShieldCheck,
  default: BookOpen,
};

function iconFor(slug: string) {
  return ICONS[slug] ?? ICONS.default;
}

function HelpIndex() {
  const initialData = Route.useLoaderData() as { categories: HelpCategory[]; articles: Array<{ id: string; slug: string; category_id: string; updated_at: string; title: string; excerpt: string | null }> };
  const fetchTree = useServerFn(getHelpTree);
  const { data, isLoading } = useQuery({
    queryKey: ["help-tree", "en"],
    queryFn: () => fetchTree({ data: { locale: "en" } }),
    initialData,
    staleTime: 60_000,
  });
  const [q, setQ] = useState("");

  const roots = useMemo<HelpCategory[]>(
    () => (data?.categories ?? []).filter((c) => c.parent_id === null),
    [data],
  );
  const childrenOf = (id: string) =>
    (data?.categories ?? []).filter((c) => c.parent_id === id);
  const articles = data?.articles ?? [];

  const filteredArticles = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return [] as typeof articles;
    return articles
      .filter((a: typeof articles[number]) =>
        a.title.toLowerCase().includes(term) || (a.excerpt ?? "").toLowerCase().includes(term))
      .slice(0, 8);
  }, [q, articles]);

  const articlePath = (a: { category_id: string; slug: string }) => {
    const cats = data?.categories ?? [];
    const path: string[] = [];
    let cur = cats.find((c) => c.id === a.category_id);
    while (cur) {
      path.unshift(cur.slug);
      cur = cur.parent_id ? cats.find((c) => c.id === cur!.parent_id) : undefined;
    }
    return "/help/" + [...path, a.slug].join("/");
  };

  return (
    <SiteLayout>
      {/* HERO */}
      <section className="bg-background">
        <div className="mx-auto max-w-5xl px-4 pb-12 pt-20 text-center md:px-6">
          <p className="inline-flex items-center justify-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-brand-lime" />
            Help Center
          </p>
          <h1 className="mt-4 font-display text-4xl leading-[1.05] md:text-6xl">
            How can we <em className="italic">help?</em>
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-base text-muted-foreground md:text-lg">
            Search guides, tutorials and walkthroughs for every corner of Banana Cards.
          </p>

          {/* SEARCH */}
          <div className="relative mx-auto mt-8 max-w-2xl">
            <Search className="pointer-events-none absolute left-5 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search for articles, e.g. ‘share my card’"
              className="w-full rounded-xl bg-muted px-14 py-5 text-base outline-none transition focus:bg-card focus:ring-2 focus:ring-foreground"
            />
            {filteredArticles.length > 0 && (
              <div className="absolute left-0 right-0 top-full z-10 mt-2 overflow-hidden rounded-2xl bg-card text-left">
              {filteredArticles.map((a: typeof articles[number]) => (
                  <Link
                    key={a.id}
                    to={articlePath(a) as any}
                    className="flex items-start justify-between gap-3 px-5 py-3 transition hover:bg-muted"
                  >
                    <div>
                      <div className="text-sm font-bold">{a.title}</div>
                      {a.excerpt && <div className="mt-0.5 line-clamp-1 text-xs text-muted-foreground">{a.excerpt}</div>}
                    </div>
                    <ArrowUpRight className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="bg-muted">
        <div className="mx-auto max-w-6xl px-4 py-16 md:px-6">
          <div className="flex items-end justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Browse by topic</p>
              <h2 className="mt-2 font-display text-3xl md:text-4xl">Pick a <em className="italic">category</em>.</h2>
            </div>
          </div>

          {isLoading ? (
            <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="h-44 animate-pulse rounded-2xl bg-card" />
              ))}
            </div>
          ) : roots.length === 0 ? (
            <div className="mt-8 rounded-2xl bg-card p-10 text-center">
              <p className="font-display text-xl">No articles yet.</p>
              <p className="mt-2 text-sm text-muted-foreground">
                Run the help-center SQL to create the tables and seed the first articles.
              </p>
            </div>
          ) : (
            <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {roots.map((cat) => {
                const Icon = iconFor(cat.slug);
                const subs = childrenOf(cat.id);
                const catArticles = articles.filter((a: typeof articles[number]) => a.category_id === cat.id);
                return (
                  <div
                    key={cat.id}
                    className="group flex h-full flex-col rounded-2xl bg-card p-6 transition hover:-translate-y-0.5"
                  >
                    <Link
                      to={`/help/${cat.slug}` as any}
                      className="flex items-start justify-between"
                      aria-label={cat.name}
                    >
                      <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-zinc-50 text-foreground">
                        <Icon className="h-5 w-5" />
                      </div>
                      <ArrowUpRight className="h-4 w-4 text-muted-foreground transition group-hover:rotate-12" />
                    </Link>
                    <Link to={`/help/${cat.slug}` as any} className="mt-5 block">
                      <h3 className="font-display text-xl transition hover:underline">{cat.name}</h3>
                      {cat.description && (
                        <p className="mt-1.5 line-clamp-2 text-sm text-muted-foreground">
                          {cat.description}
                        </p>
                      )}
                    </Link>
                    {subs.length > 0 ? (
                      <ul className="mt-5 space-y-1.5 text-sm">
                        {subs.slice(0, 4).map((s) => (
                          <li key={s.id}>
                            <Link
                              to={`/help/${cat.slug}/${s.slug}` as any}
                              className="flex items-center gap-1.5 text-foreground/80 transition hover:text-foreground hover:underline"
                            >
                              <ChevronRight className="h-3.5 w-3.5 opacity-50" />
                              {s.name}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    ) : catArticles.length > 0 ? (
                      <ul className="mt-5 space-y-1.5 text-sm">
                        {catArticles.slice(0, 4).map((a: typeof articles[number]) => (
                          <li key={a.id}>
                            <Link
                              to={`/help/${cat.slug}/${a.slug}` as any}
                              className="flex items-center gap-1.5 text-foreground/80 transition hover:text-foreground hover:underline"
                            >
                              <ChevronRight className="h-3.5 w-3.5 opacity-50" />
                              {a.title}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    ) : null}
                    <div className="mt-auto pt-5 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                      {cat.article_count ?? 0} article{(cat.article_count ?? 0) === 1 ? "" : "s"}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-background">
        <div className="mx-auto max-w-5xl px-4 py-16 md:px-6">
          <div className="rounded-3xl bg-muted p-8 md:p-12">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Still stuck?</p>
            <h3 className="mt-2 font-display text-2xl md:text-3xl">
              Our team is one message <em className="italic">away.</em>
            </h3>
            <p className="mt-3 max-w-xl text-sm text-muted-foreground">
              Can't find what you're looking for? Reach out — we read every message.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/contact" className="inline-flex items-center gap-2 rounded-xl bg-foreground px-5 py-3 text-sm font-bold text-background transition hover:opacity-90">
                Contact support <ArrowUpRight className="h-4 w-4" />
              </Link>
              <a href="mailto:hello@banana.cards" className="inline-flex items-center gap-2 rounded-xl bg-card px-5 py-3 text-sm font-bold transition hover:bg-foreground hover:text-background">
                hello@banana.cards
              </a>
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}